// frontend/src/features/landingPages/sectionCatalog.js
//
// M12 — Landing Pages & Conversion (P1 prototype)
// Catalog of landing-page section building blocks used to *structure the
// outline* of a page. This is UI-only scaffolding: no hosting, publishing,
// or backend persistence is implemented in this prototype.

/**
 * Each section type describes a reusable block an author can add to a landing
 * page outline. `defaultContent` seeds the editable outline fields so the
 * author can immediately start shaping copy structure (not final content).
 */
/**
 * Lead form fields visitors can fill in. Must match the server catalog
 * (backend/src/services/landingPages/formFields.js). Work email is required.
 */
export const FORM_FIELD_OPTIONS = [
  { key: 'full_name', label: 'Full name' },
  { key: 'work_email', label: 'Work email', required: true },
  {
    key: 'subject',
    label: 'Subject',
    required: true,
    type: 'select',
    options: [
      'Request a Demo',
      'Sales & Pricing',
      'Product Information',
      'Partnership Opportunity',
      'General Enquiry',
    ],
  },
  { key: 'company', label: 'Company' },
  { key: 'job_title', label: 'Job title' },
  { key: 'phone', label: 'Phone' },
  { key: 'message', label: 'Message' },
  { key: 'book_meeting', label: 'Book a meeting' },
];

export const SECTION_TYPES = [
  {
    type: 'hero',
    label: 'Hero',
    icon: 'fas fa-bullhorn',
    description: 'Headline, subheading, and primary call-to-action at the top of the page.',
    defaultContent: {
      heading: 'Your value proposition in one line',
      subheading: 'A short supporting sentence that explains the offer.',
      ctaLabel: 'Get started',
    },
    fields: [
      { key: 'heading', label: 'Headline', type: 'text' },
      { key: 'subheading', label: 'Subheading', type: 'textarea' },
      { key: 'ctaLabel', label: 'CTA button label', type: 'text' },
    ],
  },
  {
    type: 'features',
    label: 'Feature Grid',
    icon: 'fas fa-th-large',
    description: 'A set of key benefits or features, typically shown as cards.',
    defaultContent: {
      heading: 'Why teams choose us',
      items: 'Benefit one\nBenefit two\nBenefit three',
    },
    fields: [
      { key: 'heading', label: 'Section heading', type: 'text' },
      { key: 'items', label: 'Feature items (one per line)', type: 'textarea' },
    ],
  },
  {
    type: 'leadForm',
    label: 'Lead Capture Form',
    icon: 'fas fa-wpforms',
    description: 'Form visitors fill in; submissions become leads in your pipeline.',
    defaultContent: {
      heading: 'Request a demo',
      fields: 'Full name\nWork email\nSubject\nCompany',
      consentNote: '',
    },
    fields: [
      { key: 'heading', label: 'Form heading', type: 'text' },
      { key: 'fields', label: 'Form fields', type: 'formFields' },
      {
        key: 'consentNote',
        label: 'Consent text (leave empty for the default wording)',
        type: 'textarea',
      },
    ],
  },
  {
    type: 'testimonial',
    label: 'Testimonial',
    icon: 'fas fa-quote-right',
    description: 'Social proof quote from a customer or partner.',
    defaultContent: {
      quote: 'This product transformed how we work.',
      attribution: 'Jane Doe, Head of Growth',
    },
    fields: [
      { key: 'quote', label: 'Quote', type: 'textarea' },
      { key: 'attribution', label: 'Attribution', type: 'text' },
    ],
  },
  {
    type: 'cta',
    label: 'Call To Action',
    icon: 'fas fa-hand-pointer',
    description: 'A focused conversion prompt placed mid or end of the page.',
    defaultContent: {
      heading: 'Ready to get started?',
      ctaLabel: 'Book a call',
    },
    fields: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'ctaLabel', label: 'CTA button label', type: 'text' },
    ],
  },
  {
    type: 'faq',
    label: 'FAQ',
    icon: 'fas fa-circle-question',
    description: 'Common questions and answers to reduce conversion friction.',
    defaultContent: {
      heading: 'Frequently asked questions',
      items: 'How does pricing work?\nIs there a free trial?\nHow is my data handled?',
    },
    fields: [
      { key: 'heading', label: 'Section heading', type: 'text' },
      { key: 'items', label: 'Questions (one per line)', type: 'textarea' },
    ],
  },
  {
    type: 'footer',
    label: 'Footer',
    icon: 'fas fa-shoe-prints',
    description: 'Closing links, legal notes, and secondary navigation.',
    defaultContent: {
      note: 'Company name · Privacy · Terms',
    },
    fields: [{ key: 'note', label: 'Footer note', type: 'text' }],
  },
];

export function getSectionType(type) {
  return SECTION_TYPES.find((s) => s.type === type) || null;
}

/**
 * Build a new outline section instance from a section type.
 * Uses a timestamp+random id — adequate for a client-only prototype.
 */
export function createSection(type) {
  const def = getSectionType(type);
  if (!def) return null;
  return {
    id: `sec_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    type: def.type,
    label: def.label,
    content: { ...def.defaultContent },
  };
}
